<script>
	import { formatDateTime, yesNoToBoolean } from "src/utils/misc";
	import { onMount, onDestroy } from "svelte";
	import { navigate } from "svelte-routing";
	import { Col, Row, DropdownItem } from "@sveltestrap/sveltestrap";
	import CustomTable from "../../../../components/CustomTable/CustomTable.svelte";
	import CustomSearch from "../../../../components/CustomSearch/CustomSearch.svelte";
	import StatusBadgeItem from "../../../../components/CustomSearch/StatusBadgeItem.svelte";
	import StatusBadgeSelection from "../../../../components/CustomSearch/StatusBadgeSelection.svelte";
	import CustomDatePicker from "../../../../components/CustomDatePicker/CustomDatePicker.svelte";
	import CustomButton from "../../../../components/CustomButton/CustomButton.svelte";
	import SearchIcon from "../../../../components/SearchIcon/SearchIcon.svelte";
	import TableDropdown from "../../../../components/TableDropdown/TableDropdown.svelte";
	import AddIssuesToWorkOrderModal from "./AddIssuesToWorkOrderModal.svelte";
	import IssueDetailSidePanel from "./IssueDetailSidePanel.svelte";
	import MixedKindWarningModal from "./MixedKindWarningModal.svelte";
	import CancelIssueModal from "./CancelIssueModal.svelte";
	import Loader from "src/components/Loader/Loader.svelte";
	import { isMnrPhase2Enabled } from "src/config/flag-config";
	import {
		addIssuesToWorkOrder,
		changeIssueStatus,
		getIssueById,
		getVehicleIssues,
		getIssueTypes,
		getWorkOrderById,
		setIssuesHandoverFlag,
	} from "src/services/maintenanceRepair";
	import { rejectedIssuesMessage } from "./workOrderErrors";
	import {
		serviceCategoriesToOptions,
		issueStatusOptions,
		flaggedOptions,
		pageSizeOptions,
	} from "src/utils/options";
	import { fetchServiceCategories } from "src/services/lookupCache";
	import { formatErrorMessage } from "src/utils/misc";
	import {
		cachedServiceCategories,
		initPage,
		sizeSelect,
		totalPages,
		totalRecords,
		toastMessage,
	} from "../../../../store/store";

	export let vehicleId = "";
	// Reports a load error to the vehicle page's banner ("" clears it).
	export let onError = (_message) => {};

	const issueStatusVariantByLabel = Object.fromEntries(
		issueStatusOptions.map((option) => [option.label, option.variant])
	);
	const issueStatusLabelByValue = Object.fromEntries(
		issueStatusOptions.map((option) => [option.value, option.label])
	);
	// The dropdown lists labels, so a picked transition comes back as one and
	// has to be turned into the value the API expects.
	const issueStatusValueByLabel = Object.fromEntries(
		issueStatusOptions.map((option) => [option.label, option.value])
	);

	let categoriesError = false;
	$: serviceCategorySelectOptions = serviceCategoriesToOptions(
		$cachedServiceCategories
	);
	// The API sends the category code (e.g. "NonWorkshop"); look up either.
	$: serviceCategoryByCodeOrLabel = Object.fromEntries(
		($cachedServiceCategories ?? []).flatMap((category) => [
			[category.code, category],
			[category.label, category],
		])
	);

	function serviceCategoryLabel(serviceCategory) {
		return (
			serviceCategoryByCodeOrLabel[serviceCategory]?.label ??
			serviceCategory
		);
	}
	// The Issues list filters by issueTypeId.
	let issueTypeSelectOptions = [];
	const issueStatusSelectOptions = issueStatusOptions.map((option) => ({
		value: option.value,
		label: option.label,
		variant: option.variant,
	}));
	const issuePageSizeOptions = [
		{ value: "5", label: "Show 5" },
		...pageSizeOptions,
	];

	let isMounted = false;
	let issues = [];
	let totalCount = 0;
	let isLoading = false;
	let loadError = false;
	let latestRequestId = 0;
	let knownIssuesById = {};

	// Terminal statuses can't be flagged for handover (FRNT-6397).
	const CLOSED_ISSUE_STATUSES = ["Completed", "Cancelled"];

	let isSelectMode = false;
	// Declared before onMount, which can enter select mode straight away
	// (?addToWorkOrder) and bumps this counter.
	let selectSessionId = 0;

	let selectModeKind = "workOrder";
	let selectedIssueIds = [];
	let isAddToWorkOrderModalOpen = false;
	let isSubmittingPresetWorkOrder = false;
	let presetWorkOrderId = "";
	// Resolves to the preset work order's kind ("workshop"/"nonWorkshop"),
	// or null when it can't be loaded and the backend has to decide.
	let presetWorkOrderKind = Promise.resolve(null);

	let isDetailPanelOpen = false;
	let selectedIssue = null;
	let selectedIssueId = null;
	let isDetailLoading = false;
	let isDetailNotFound = false;
	let detailError = "";
	let latestDetailRequestId = 0;

	let isMixedKindWarningOpen = false;

	let isCancelModalOpen = false;
	let cancellingIssue = null;
	let isCancellingIssue = false;
	let isChangingStatus = false;
	let cancelIssueError = "";
	let cancelSessionId = 0;

	function handleStatusOptionSelect(row, option) {
		const targetState = issueStatusValueByLabel[option] || option;
		if (targetState === "Cancelled") {
			cancellingIssue = {
				id: row.id,
				status: knownIssuesById[row.id]?.status ?? row.status,
			};
			cancelIssueError = "";
			isCancelModalOpen = true;
			return;
		}
		changeIssueStatusTo(row, targetState);
	}

	function issueKind(serviceCategory) {
		const code =
			serviceCategoryByCodeOrLabel[serviceCategory]?.code ??
			serviceCategory;
		return code === "NonWorkshop" || code === "Non-workshop"
			? "nonWorkshop"
			: "workshop";
	}

	let issueIdInput = "";
	let addedByEmailInput = "";
	let dateReportedFrom = "";
	let dateReportedTo = "";
	let flaggedFilter = "";
	let serviceCategoryFilter = "";
	let issueTypeFilter = "";

	let issueTypesLoaded = false;

	// Issue types load for the chosen category (all types when none is
	// chosen) and reset whenever it changes.
	async function loadIssueTypeOptions(category) {
		issueTypeFilter = "";
		issueTypeSelectOptions = [];
		issueTypesLoaded = false;
		const types = await getIssueTypes(category);
		if (category !== selectedServiceCategory) return;
		issueTypeSelectOptions = types.map((type) => ({
			value: String(type.id),
			label: type.label,
		}));
		issueTypesLoaded = true;
	}
	// CustomSearch binds the whole option ({ value, label }), or null when cleared.
	$: selectedServiceCategory = serviceCategoryFilter?.value ?? "";
	$: loadIssueTypeOptions(selectedServiceCategory);
	let statusFilter = "";

	let appliedFilters = {
		code: "",
		addedByEmail: "",
		dateCreatedFrom: "",
		dateCreatedTo: "",
		handoverFlagged: null,
		serviceCategory: "",
		issueTypeId: "",
		status: "",
	};

	const SORTABLE_COLUMNS = new Set(["createdAt"]);
	const urlParams = new URLSearchParams(window.location.search);
	let sortCol = SORTABLE_COLUMNS.has(urlParams.get("sortCol"))
		? urlParams.get("sortCol")
		: "";
	let sortDir = sortCol
		? urlParams.get("sortDir") === "ascending"
			? "asc"
			: "desc"
		: "";

	function buildQueryParams(filters, sort, page, size, backlogOnly) {
		const optionalParams = {
			...filters,
			status: backlogOnly ? "Backlog" : filters.status,
			sortCol: sort.sortCol,
			sortDir: sort.sortDir,
		};
		return {
			page,
			size,
			...Object.fromEntries(
				Object.entries(optionalParams).filter(
					([, value]) =>
						value !== "" && value !== null && value !== undefined
				)
			),
		};
	}

	async function loadIssues(forVehicleId, params) {
		const requestId = ++latestRequestId;
		isLoading = true;
		loadError = false;
		try {
			const res = await getVehicleIssues(forVehicleId, params);
			if (requestId !== latestRequestId) return;
			issues = res.data?.items ?? [];
			totalCount = res.data?.totalCount ?? issues.length;
			knownIssuesById = {
				...knownIssuesById,
				...Object.fromEntries(issues.map((issue) => [issue.id, issue])),
			};
			const lastPage = Math.max(1, Math.ceil(totalCount / params.size));
			if (params.page > lastPage) initPage.set(lastPage);
			onError("");
		} catch (error) {
			if (requestId !== latestRequestId) return;
			console.error(error);
			issues = [];
			totalCount = 0;
			loadError = true;
			onError(formatErrorMessage(error) || "Couldn't load issues.");
		} finally {
			if (requestId === latestRequestId) isLoading = false;
		}
	}

	let currentVehicleId = vehicleId;
	$: if (vehicleId !== currentVehicleId) {
		currentVehicleId = vehicleId;
		resetVehicleState();
	}

	function resetVehicleState() {
		selectSessionId++;
		issues = [];
		totalCount = 0;
		knownIssuesById = {};
		isSelectMode = false;
		selectedIssueIds = [];
		presetWorkOrderId = "";
		isAddToWorkOrderModalOpen = false;
		isMixedKindWarningOpen = false;
		isCancelModalOpen = false;
		cancellingIssue = null;
		isCancellingIssue = false;
		cancelSessionId++;
		isDetailPanelOpen = false;
		selectedIssue = null;
		selectedIssueId = null;
		latestDetailRequestId++;
		initPage.set(1);
	}

	$: queryParams = buildQueryParams(
		appliedFilters,
		{ sortCol, sortDir },
		$initPage,
		Number($sizeSelect),
		isSelectMode && selectModeKind === "workOrder"
	);
	$: if (isMounted) loadIssues(vehicleId, queryParams);

	$: totalRecords.set(totalCount);
	$: totalPages.set(Math.max(1, Math.ceil(totalCount / Number($sizeSelect))));

	onMount(() => {
		initPage.set(1);
		sizeSelect.set("5");
		isMounted = true;
		fetchServiceCategories().catch(() => (categoriesError = true));

		// Landed here via WorkOrderDetail's "Add Issue" button — open
		// straight into the "Add issues to work order" selection state.
		// The param is stripped right after so switching tabs away and
		// back doesn't re-trigger it on remount.
		const params = new URLSearchParams(window.location.search);
		const addToWorkOrderId = params.get("addToWorkOrder");
		if (addToWorkOrderId && isMnrPhase2Enabled) {
			presetWorkOrderId = addToWorkOrderId;
			presetWorkOrderKind = loadWorkOrderKind(addToWorkOrderId);
			handleEnterSelectMode("workOrder");
			params.delete("addToWorkOrder");
			const newSearch = params.toString();
			window.history.replaceState(
				null,
				"",
				window.location.pathname + (newSearch ? `?${newSearch}` : "")
			);
		}
	});

	onDestroy(() => {
		latestRequestId++;
		cancelSessionId++;
		latestDetailRequestId++;
		selectSessionId++;
		initPage.set(1);
		sizeSelect.set("20");
		totalPages.set(1);
		totalRecords.set(0);
	});

	function handleSearch() {
		appliedFilters = {
			code: issueIdInput.trim().toUpperCase(),
			addedByEmail: addedByEmailInput.trim().toLowerCase(),
			dateCreatedFrom: dateReportedFrom,
			dateCreatedTo: dateReportedTo,
			handoverFlagged: yesNoToBoolean(flaggedFilter),
			serviceCategory: selectedServiceCategory,
			issueTypeId: issueTypeFilter?.value ?? "",
			status: statusFilter,
		};
		initPage.set(1);
	}

	$: hasAppliedFilters = Object.values(appliedFilters).some(
		(value) => value !== "" && value !== null
	);

	const toOption = (options, value) =>
		options.find((option) => option.value === value) ?? null;

	function handlePageSizeChange(option) {
		if (option.value === $sizeSelect) return;
		sizeSelect.set(option.value);
		initPage.set(1);
	}

	let datePickerKey = 0;

	$: hasFilters =
		hasAppliedFilters ||
		[
			issueIdInput,
			addedByEmailInput,
			dateReportedFrom,
			flaggedFilter,
			serviceCategoryFilter,
			issueTypeFilter,
			statusFilter,
		].some(Boolean);

	function handleClear() {
		issueIdInput = "";
		addedByEmailInput = "";
		dateReportedFrom = "";
		dateReportedTo = "";
		flaggedFilter = "";
		serviceCategoryFilter = "";
		issueTypeFilter = "";
		statusFilter = "";
		datePickerKey++;
		handleSearch();
	}

	// Stays on the current page: sorting doesn't change the total.
	function handleSort(event) {
		sortCol = event.detail.column;
		sortDir = event.detail.direction === "descending" ? "desc" : "asc";
	}

	function handleCreateIssue() {
		navigate(`/maintenance-repair/${vehicleId}/issues/create-issue`);
	}

	async function handleEnterSelectMode(kind) {
		const sessionId = ++selectSessionId;
		isSelectMode = true;
		selectModeKind = kind;
		selectedIssueIds = [];
		initPage.set(1);

		if (kind === "flag") {
			if (appliedFilters.handoverFlagged === false) return;
			try {
				const res = await getVehicleIssues(
					vehicleId,
					buildQueryParams(
						{ ...appliedFilters, handoverFlagged: true },
						{ sortCol, sortDir },
						1,
						100,
						false
					)
				);
				if (sessionId !== selectSessionId) return;
				selectedIssueIds = (res.data?.items ?? []).map(
					(issue) => issue.id
				);
			} catch (error) {
				if (sessionId !== selectSessionId) return;
				console.error(error);
				toastMessage.set({
					type: "danger",
					message:
						formatErrorMessage(error) ||
						"Couldn't load flagged issues. Please try again.",
				});
				handleCancelSelectMode();
			}
		}
	}

	function handleCancelSelectMode() {
		selectSessionId++;
		isSelectMode = false;
		selectedIssueIds = [];
		presetWorkOrderId = "";
		initPage.set(1);
	}

	function handleToggleIssueSelected(row) {
		if (selectModeKind === "workOrder" && row.status !== "Backlog") return;
		if (selectModeKind === "flag" && !row.flaggable) return;
		selectedIssueIds = selectedIssueIds.includes(row.id)
			? selectedIssueIds.filter((id) => id !== row.id)
			: [...selectedIssueIds, row.id];
	}

	function handleToggleSelectAll() {
		const selectableIds = selectableTableRows.map((row) => row.id);
		selectedIssueIds = allVisibleSelected
			? selectedIssueIds.filter((id) => !selectableIds.includes(id))
			: [...new Set([...selectedIssueIds, ...selectableIds])];
	}

	function hasMixedKindSelection() {
		const kinds = new Set(
			selectedIssueIds
				.map((id) => knownIssuesById[id])
				.filter(Boolean)
				.map((issue) => issueKind(issue.serviceCategory))
		);
		return kinds.size > 1;
	}

	$: selectedFlowType =
		issueKind(knownIssuesById[selectedIssueIds[0]]?.serviceCategory) ===
		"nonWorkshop"
			? "NonWorkshop"
			: "Workshop";

	function handleOpenAddToWorkOrderModal() {
		if (hasMixedKindSelection()) {
			isMixedKindWarningOpen = true;
			return;
		}
		if (presetWorkOrderId) {
			addSelectedIssuesToPresetWorkOrder();
			return;
		}
		isAddToWorkOrderModalOpen = true;
	}

	async function loadWorkOrderKind(workOrderId) {
		try {
			const { data } = await getWorkOrderById(workOrderId);
			if (!data?.flowType) return null;
			return data.flowType === "Workshop" ? "workshop" : "nonWorkshop";
		} catch {
			return null;
		}
	}

	async function addSelectedIssuesToPresetWorkOrder() {
		if (isSubmittingPresetWorkOrder) return;
		isSubmittingPresetWorkOrder = true;
		try {
			// The backend rejects a kind mismatch per issue, which only
			// surfaces as a "Not added" toast — catch it here instead.
			const workOrderKind = await presetWorkOrderKind;
			const selectedKind = issueKind(
				knownIssuesById[selectedIssueIds[0]]?.serviceCategory
			);
			if (workOrderKind && workOrderKind !== selectedKind) {
				isMixedKindWarningOpen = true;
				return;
			}
			const res = await addIssuesToWorkOrder(presetWorkOrderId, {
				issueIds: selectedIssueIds,
				addedVia: "Grouping",
				issues: selectedIssueIds
					.map((id) => knownIssuesById[id])
					.filter(Boolean),
			});
			if (res.error) {
				// 29205 = a selected issue's kind doesn't match the work order's.
				if (res.error.code === 29205) {
					isMixedKindWarningOpen = true;
					return;
				}
				onError(
					res.error.message ||
						"Something went wrong. Please try again."
				);
				return;
			}
			handleAddToWorkOrderConfirm({
				detail: {
					destination: "existing",
					workOrderId: res.data.workOrder.id,
					rejected: res.data.rejected,
				},
			});
		} finally {
			isSubmittingPresetWorkOrder = false;
		}
	}

	function closeAddToWorkOrderModal() {
		isAddToWorkOrderModalOpen = false;
	}

	// Mirrors WorkOrderDetail.svelte's confirmMoveToAnotherWorkOrder — both
	// paths land on the work order the issues just moved to.
	function handleAddToWorkOrderConfirm({
		detail: { destination, workOrderId, rejected = [] },
	}) {
		isAddToWorkOrderModalOpen = false;
		handleCancelSelectMode();

		const successMessage =
			destination === "new" ? "Work order created" : "Issue(s) added";
		toastMessage.set(
			rejected.length
				? {
						type: "danger",
						message: `${successMessage} ${rejectedIssuesMessage(
							rejected,
							Object.fromEntries(
								Object.values(knownIssuesById).map((issue) => [
									issue.id,
									issue.code,
								])
							)
						)}`,
					}
				: { type: "success", message: successMessage }
		);
		navigate(`/maintenance-repair/${vehicleId}/details/${workOrderId}`);
	}

	async function loadIssueDetail(issueId) {
		const requestId = ++latestDetailRequestId;
		selectedIssue = null;
		isDetailLoading = true;
		isDetailNotFound = false;
		detailError = "";
		try {
			const res = await getIssueById(issueId);
			if (requestId !== latestDetailRequestId) return;
			selectedIssue = {
				...res.data,
				serviceCategory: serviceCategoryLabel(
					res.data?.serviceCategory
				),
			};
		} catch (error) {
			if (requestId !== latestDetailRequestId) return;
			if (
				error?.response?.status === 404 ||
				error?.response?.data?.code === 29101
			) {
				isDetailNotFound = true;
			} else {
				console.error(error);
				detailError =
					formatErrorMessage(error) ?? "Something went wrong.";
			}
		} finally {
			if (requestId === latestDetailRequestId) isDetailLoading = false;
		}
	}

	function handleOpenIssueDetail(row) {
		selectedIssueId = row.id;
		isDetailPanelOpen = true;
		loadIssueDetail(row.id);
	}

	const CANCEL_ISSUE_ERROR_MESSAGES = {
		29101: "This issue no longer exists.",
		29103: "This issue is on an open work order and can't be cancelled.",
	};

	function applyIssueStatus(issueId, status) {
		const update = (issue) => ({
			...issue,
			status,
			allowedTransitions: [],
		});
		issues = issues.map((issue) =>
			issue.id === issueId ? update(issue) : issue
		);
		if (knownIssuesById[issueId]) {
			knownIssuesById = {
				...knownIssuesById,
				[issueId]: update(knownIssuesById[issueId]),
			};
		}
		if (selectedIssue?.id === issueId) {
			selectedIssue = update(selectedIssue);
		}
	}

	function handoverFlagErrorMessage(error) {
		return error?.message || "Something went wrong. Please try again.";
	}

	function rejectedFlagsMessage(rejected) {
		return rejected
			.map((entry) => {
				const code =
					knownIssuesById[entry.issueId]?.code || entry.issueId;
				return `${code}: ${entry.reason || "couldn't be flagged"}`;
			})
			.join("; ");
	}

	function applyHandoverFlag(issueId, handoverFlagged) {
		issues = issues.map((issue) =>
			issue.id === issueId ? { ...issue, handoverFlagged } : issue
		);
		if (knownIssuesById[issueId]) {
			knownIssuesById = {
				...knownIssuesById,
				[issueId]: { ...knownIssuesById[issueId], handoverFlagged },
			};
		}
		if (selectedIssue?.id === issueId) {
			selectedIssue = { ...selectedIssue, handoverFlagged };
		}
	}

	function refetchIfFlagFilterMismatch(handoverFlagged) {
		if (
			appliedFilters.handoverFlagged !== null &&
			appliedFilters.handoverFlagged !== handoverFlagged
		) {
			loadIssues(vehicleId, queryParams);
		}
	}

	function handleIssueFlagToggled({ detail: { issueId, handoverFlagged } }) {
		applyHandoverFlag(issueId, handoverFlagged);
		refetchIfFlagFilterMismatch(handoverFlagged);
	}

	async function changeIssueStatusTo(row, targetState) {
		if (isChangingStatus) return;
		const issueId = row.id;
		const currentState = knownIssuesById[issueId]?.status ?? row.status;
		const sessionId = cancelSessionId;
		isChangingStatus = true;
		try {
			const res = await changeIssueStatus(issueId, {
				currentState,
				targetState,
			});
			if (sessionId !== cancelSessionId) return;
			if (res.error) {
				toastMessage.set({
					type: "danger",
					message:
						res.error.message ||
						CANCEL_ISSUE_ERROR_MESSAGES[res.error.code] ||
						"Something went wrong. Please try again.",
				});
				return;
			}
			const status = res.data?.status ?? targetState;
			toastMessage.set({
				type: "success",
				message: `Issue status changed to ${
					issueStatusLabelByValue[status] || status
				}.`,
			});
			// The response carries no allowedTransitions, so the list is
			// refetched rather than guessed — otherwise the status dropdown
			// loses its options and can't be used again until a reload.
			await loadIssues(vehicleId, queryParams);
			if (selectedIssueId === issueId) loadIssueDetail(issueId);
		} finally {
			if (sessionId === cancelSessionId) isChangingStatus = false;
		}
	}

	async function handleConfirmCancelIssue({ detail: { reasonCode } }) {
		if (isCancellingIssue || !cancellingIssue) return;
		const { id: issueId, status: currentState } = cancellingIssue;
		const sessionId = cancelSessionId;
		isCancellingIssue = true;
		try {
			const res = await changeIssueStatus(issueId, {
				currentState,
				targetState: "Cancelled",
				reasonCode,
			});
			if (sessionId !== cancelSessionId) return;
			if (res.error) {
				// Stays open with the error in the modal's banner.
				cancelIssueError =
					res.error.message ||
					CANCEL_ISSUE_ERROR_MESSAGES[res.error.code] ||
					"Something went wrong. Please try again.";
				return;
			}
			isCancelModalOpen = false;
			cancellingIssue = null;
			const status = res.data?.status ?? "Cancelled";
			applyIssueStatus(issueId, status);
			toastMessage.set({ type: "success", message: "Issue cancelled." });
			if (appliedFilters.status && appliedFilters.status !== status) {
				loadIssues(vehicleId, queryParams);
			}
		} finally {
			if (sessionId === cancelSessionId) isCancellingIssue = false;
		}
	}

	let isBulkFlagging = false;

	async function handleConfirmBulkFlag() {
		if (isBulkFlagging) return;
		const sessionId = selectSessionId;
		isBulkFlagging = true;
		try {
			const ids = selectedIssueIds;
			const res = await setIssuesHandoverFlag(ids, true);
			if (sessionId !== selectSessionId) return;
			if (res.error) {
				toastMessage.set({
					type: "danger",
					message: res.error.rejected?.length
						? rejectedFlagsMessage(res.error.rejected)
						: handoverFlagErrorMessage(res.error),
				});
				return;
			}

			(res.data?.updated ?? []).forEach((issueId) =>
				applyHandoverFlag(issueId, true)
			);
			refetchIfFlagFilterMismatch(true);

			const rejected = res.data?.rejected ?? [];
			if (rejected.length > 0) {
				toastMessage.set({
					type: "danger",
					message: rejectedFlagsMessage(rejected),
				});
			} else {
				toastMessage.set({
					type: "success",
					message: "Issues flagged.",
				});
			}

			handleCancelSelectMode();
		} finally {
			isBulkFlagging = false;
		}
	}

	$: pagedIssues = issues.filter((issue) =>
		isSelectMode && selectModeKind === "workOrder"
			? issue.status === "Backlog"
			: true
	);

	function toDisplayIssue(issue) {
		return {
			id: issue.id,
			code: issue.code,
			serviceCategory: serviceCategoryLabel(issue.serviceCategory),
			issueType: issue.issueType?.name,
			issueDescription: issue.issueDescription,
			status: issueStatusLabelByValue[issue.status] || issue.status,
			allowedTransitions: (issue.allowedTransitions || []).map(
				(transition) =>
					issueStatusLabelByValue[transition] || transition
			),
			createdAt: issue.createdAt,
			createdAtDisplay: formatDateTime(issue.createdAt),
			workOrderDisplay: issue.workOrder ? issue.workOrder.code : "–",
			workOrderId: issue.workOrder?.id,
			createdBy: issue.createdBy,
			handoverFlagged: isMnrPhase2Enabled
				? issue.handoverFlagged
				: undefined,
			flaggable: !CLOSED_ISSUE_STATUSES.includes(
				issueStatusLabelByValue[issue.status] || issue.status
			),
		};
	}

	$: tableRows = pagedIssues.map(toDisplayIssue);

	$: selectableTableRows =
		selectModeKind === "workOrder"
			? tableRows.filter((row) => row.status === "Backlog")
			: tableRows.filter((row) => row.flaggable);
	$: allVisibleSelected =
		selectableTableRows.length > 0 &&
		selectableTableRows.every((row) => selectedIssueIds.includes(row.id));

	$: tableColumns = [
		...(isSelectMode
			? [
					{
						key: "id",
						label: "",
						type: "rowCheckbox",
						event: handleToggleIssueSelected,
						options: selectedIssueIds,
						headerChecked: allVisibleSelected,
						onHeaderToggle: handleToggleSelectAll,
						...(selectModeKind === "workOrder"
							? {
									selectableKey: "status",
									selectableValue: "Backlog",
								}
							: {
									selectableKey: "flaggable",
									selectableValue: true,
								}),
					},
				]
			: []),
		{
			key: "code",
			label: "ID",
			type: "flagged",
			className: "link link-text",
			flagKey: "handoverFlagged",
			flagValue: true,
			event: handleOpenIssueDetail,
		},
		{ key: "serviceCategory", label: "Service category" },
		{ key: "issueType", label: "Issue type" },
		{
			key: "issueDescription",
			label: "Issue description",
			type: "truncateTooltip",
			className: "issue-description",
			width: "280px",
		},
		{
			key: "createdAtDisplay",
			label: "Date & time reported",
			sortCol: "createdAt",
		},
		{
			key: "status",
			label: "Status",
			type: "statusDropdown",
			options: issueStatusVariantByLabel,
			transitionsKey: "allowedTransitions",
			event: handleStatusOptionSelect,
		},
		...(isMnrPhase2Enabled
			? [
					{
						key: "workOrderDisplay",
						label: "Work order",
						type: "optionalLink",
						linkBy: "workOrderId",
						linkTo: `/maintenance-repair/${vehicleId}/details/`,
					},
				]
			: []),
		{ key: "createdBy", label: "Added by" },
	];
</script>

<Row>
	<Col xs={12}>
		<div class="filters-row fds-dropdown-list mb-2">
			<div class="filters-group">
				<div class="filter-field">
					<label for="vi-issue-id" class="visually-hidden"
						>Search by ID</label
					>
					<div class="search-input-wrapper">
						<SearchIcon />
						<input
							id="vi-issue-id"
							type="text"
							class="form-control getgo input vi-search-input"
							placeholder="Search by ID"
							bind:value={issueIdInput}
							on:keydown={(e) =>
								e.key === "Enter" && handleSearch()}
						/>
					</div>
				</div>
				<div class="filter-field">
					<label for="vi-added-by" class="visually-hidden"
						>Search by email</label
					>
					<div class="search-input-wrapper">
						<SearchIcon />
						<input
							id="vi-added-by"
							type="text"
							class="form-control getgo input vi-search-input"
							placeholder="Search by email"
							bind:value={addedByEmailInput}
							on:keydown={(e) =>
								e.key === "Enter" && handleSearch()}
						/>
					</div>
				</div>
				<div class="filter-field filter-field-date">
					{#key datePickerKey}
						<CustomDatePicker
							name="vi-date-reported"
							placeholder="Date reported"
							isRange={true}
							value={dateReportedFrom && dateReportedTo
								? [dateReportedFrom, dateReportedTo]
								: ""}
							onChange={(e) => {
								dateReportedFrom = e.detail[0];
								dateReportedTo = e.detail[1];
							}}
						/>
					{/key}
				</div>
			</div>
			{#if !isSelectMode && !isMnrPhase2Enabled}
				<div class="actions-group">
					<CustomButton
						buttonText="Create issue"
						on:click={handleCreateIssue}
					/>
				</div>
			{:else if !isSelectMode}
				<div class="actions-group">
					<div class="mr-ellipsis-toggle">
						<TableDropdown>
							<DropdownItem on:click={handleCreateIssue}>
								Create issue
							</DropdownItem>
							<DropdownItem
								on:click={() => handleEnterSelectMode("flag")}
							>
								Bulk flag issues
							</DropdownItem>
						</TableDropdown>
					</div>
					<CustomButton
						buttonText="Add issues to work order"
						on:click={() => handleEnterSelectMode("workOrder")}
					/>
				</div>
			{/if}
		</div>

		<div class="filters-row fds-dropdown-list mb-3">
			<div class="filters-group">
				<div class="filter-field filter-field-compact">
					<CustomSearch
						label="Filter by flagged status"
						placeholder="Flagged"
						items={flaggedOptions}
						value={toOption(flaggedOptions, flaggedFilter)}
						onSelect={(e) => (flaggedFilter = e.detail.value)}
						isSearchable={false}
						isClearable={false}
						chevron
					/>
				</div>
				<div class="filter-field filter-field-category">
					<CustomSearch
						label="Filter by service category"
						showSearchIcon
						chevron
						placeholder={categoriesError
							? "Failed to load categories"
							: $cachedServiceCategories?.length === 0
								? "No categories available"
								: "All service categories"}
						bind:value={serviceCategoryFilter}
						items={serviceCategorySelectOptions}
					/>
				</div>
				<div class="filter-field filter-field-issue-type">
					<CustomSearch
						label="Filter by issue type"
						showSearchIcon
						chevron
						placeholder={issueTypesLoaded &&
						!issueTypeSelectOptions.length
							? "No issue types available"
							: "All issue types"}
						bind:value={issueTypeFilter}
						items={issueTypeSelectOptions}
					/>
				</div>
				<div class="filter-field filter-field-status">
					<CustomSearch
						label="Filter by status"
						placeholder="All statuses"
						items={issueStatusSelectOptions}
						value={toOption(issueStatusSelectOptions, statusFilter)}
						onSelect={(e) => (statusFilter = e.detail.value)}
						isSearchable={false}
						isClearable={false}
						chevron
						Item={StatusBadgeItem}
						Selection={StatusBadgeSelection}
					/>
				</div>
				<div class="clear-filters">
					<CustomButton
						buttonText="Clear filters"
						color="link"
						disabled={!hasFilters}
						on:click={handleClear}
					/>
				</div>
				<CustomButton buttonText="Search" on:click={handleSearch} />
			</div>
			<div class="actions-group">
				<div class="page-size-select">
					<!-- Recreated whenever the page size changes: the dropdown can
					     miss a change that lands in the same update as another one
					     (onMount's "5" after a tab's onDestroy "20"), and kept
					     showing "Show 20" over a 5-row table. -->
					{#key $sizeSelect}
						<CustomSearch
							label="Items per page"
							items={issuePageSizeOptions}
							value={toOption(issuePageSizeOptions, $sizeSelect)}
							onSelect={(e) => handlePageSizeChange(e.detail)}
							isSearchable={false}
							isClearable={false}
							chevron
						/>
					{/key}
				</div>
			</div>
		</div>

		{#if isSelectMode}
			<div class="select-mode-row mb-3">
				<div class="select-mode-banner">
					<img
						src="/assets/icon-circle-info.svg"
						alt=""
						class="select-mode-banner-icon"
					/>
					<span>
						{selectModeKind === "flag"
							? "Select issues to flag out for the next shift to take note."
							: "Select issues to add to work order"}
					</span>
				</div>
				<div class="select-mode-actions">
					<CustomButton
						buttonText="Cancel"
						color="secondary"
						on:click={handleCancelSelectMode}
					/>
					{#if selectModeKind === "flag"}
						<CustomButton
							buttonText="Flag"
							disabled={selectedIssueIds.length === 0}
							on:click={handleConfirmBulkFlag}
						/>
					{:else}
						<CustomButton
							buttonText="Add"
							disabled={selectedIssueIds.length === 0}
							on:click={handleOpenAddToWorkOrderModal}
						/>
					{/if}
				</div>
			</div>
		{/if}

		<output class="visually-hidden">
			{isLoading ? "Loading issues" : ""}
		</output>

		{#if isLoading}
			<Loader />
		{:else}
			<div class="issues-table">
				<CustomTable
					columns={tableColumns}
					rows={tableRows}
					emptyMessage={loadError
						? "Couldn't load issues."
						: isSelectMode && selectModeKind === "workOrder"
							? "No Backlog issues are available to add."
							: hasAppliedFilters
								? "No issues match the selected filters."
								: "No issues for this vehicle."}
					useNavigation
					on:sort={handleSort}
				/>
			</div>
		{/if}
	</Col>
</Row>

<AddIssuesToWorkOrderModal
	{vehicleId}
	issueIds={selectedIssueIds}
	issues={selectedIssueIds.map((id) => knownIssuesById[id]).filter(Boolean)}
	flowType={selectedFlowType}
	isOpen={isAddToWorkOrderModalOpen}
	toggle={closeAddToWorkOrderModal}
	on:confirm={handleAddToWorkOrderConfirm}
/>

<IssueDetailSidePanel
	isOpen={isDetailPanelOpen}
	issue={selectedIssue}
	isLoading={isDetailLoading}
	isNotFound={isDetailNotFound}
	errorMessage={detailError}
	onRetry={() => loadIssueDetail(selectedIssueId)}
	{vehicleId}
	toggle={() => (isDetailPanelOpen = !isDetailPanelOpen)}
	on:flagToggled={handleIssueFlagToggled}
/>

<MixedKindWarningModal
	isOpen={isMixedKindWarningOpen}
	toggle={() => (isMixedKindWarningOpen = !isMixedKindWarningOpen)}
/>

<CancelIssueModal
	isOpen={isCancelModalOpen}
	isSubmitting={isCancellingIssue}
	submitError={cancelIssueError}
	toggle={() => (isCancelModalOpen = !isCancelModalOpen)}
	on:confirm={handleConfirmCancelIssue}
/>

<style>
	.is-loading {
		opacity: 0.5;
		pointer-events: none;
	}

	.select-mode-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 16px;
	}

	.select-mode-banner {
		display: flex;
		align-items: center;
		gap: 16px;
		flex: 1 1 auto;
		background-color: var(--fds-primary-container, #e6ebfd);
		color: var(--fds-on-primary-container, #00071f);
		border-radius: 8px;
		padding: 16px;
		font-weight: bold;
	}

	.select-mode-banner-icon {
		width: 24px;
		height: 24px;
		flex-shrink: 0;
	}

	.select-mode-actions {
		display: flex;
		gap: 16px;
		flex-shrink: 0;
	}

	/* CustomTable renders the cell, so the rule has to be global — anchored
	   under .issues-table so it can't reach anything else. */
	.issues-table :global(.issue-description) {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;

		width: 100%;
		height: 100%;
		min-height: 2em;
	}

	.select-mode-actions :global(.fds-button.btn-secondary),
	.select-mode-actions :global(.fds-button.btn-primary),
	.actions-group :global(.fds-button.btn-primary),
	.filters-group :global(.fds-button.btn-primary) {
		border: 1px solid #d3d3d3;
	}

	.select-mode-actions :global(.fds-button.btn-primary:not(:disabled)),
	.actions-group :global(.fds-button.btn-primary:not(:disabled)),
	.filters-group :global(.fds-button.btn-primary:not(:disabled)) {
		background-color: var(--fds-primary, #003bed);
		color: var(--fds-on-primary, #fafbff);
	}

	.filters-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
	}

	.filters-group {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		flex: 1 1 auto;
	}

	.actions-group {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		margin-left: auto;
	}

	.filter-field {
		flex: 0 1 200px;
		min-width: 160px;
	}

	.filter-field :global(.selectContainer input),
	.page-size-select :global(.selectContainer input) {
		padding-right: 36px !important;
		font-size: 14px !important;
		font-weight: 400 !important;
		letter-spacing: normal !important;
	}

	.filter-field-status {
		flex-basis: 150px;
		min-width: 150px;
	}

	.filters-row .filter-field :global(.selectContainer input),
	.filters-row .page-size-select :global(.selectContainer input) {
		padding-left: 12px !important;
	}

	.filters-row
		.filter-field
		:global(.search-icon-visible .selectContainer input) {
		padding-left: 36px !important;
	}

	.filters-row :global(input::placeholder) {
		font-size: 14px !important;
		font-weight: 400 !important;
	}

	.filters-row :global(input),
	.filters-row :global(.selectedItem) {
		font-size: 14px !important;
		font-weight: 400 !important;
		letter-spacing: normal !important;
	}

	.filter-field-compact {
		flex-basis: 130px;
		min-width: 130px;
	}

	.filters-group :global(.fds-button) {
		margin-inline: 0 !important;
	}

	.filter-field-category {
		flex-basis: 236px;
		min-width: 236px;
	}

	.filter-field-issue-type {
		flex-basis: 188px;
		min-width: 188px;
	}

	.filter-field-date {
		flex: 0 0 280px;
	}

	.clear-filters :global(.fds-button.btn-link) {
		background: none;
		border: none;
		color: var(--fds-on-surface, #00071f);
		text-decoration: none;
		min-width: 0;
		padding-inline: 0;
	}

	.clear-filters :global(.fds-button.btn-link:disabled) {
		background: none;
		color: var(--fds-on-surface-container-variant, #7d8c8c);
	}

	/* Let a search filter's option list grow past the field so long names
	   aren't cut off; the field keeps its width. !important beats the
	   inline width svelte-select sets. */
	.filter-field :global(.listContainer) {
		width: max-content !important;
		max-width: min(360px, 90vw);
	}

	.search-input-wrapper {
		position: relative;
	}

	.search-input-wrapper :global(.form-control.getgo.input.vi-search-input) {
		padding-left: 36px;
	}

	.mr-ellipsis-toggle :global(.table-dropdown) {
		background-color: white;
		border: 1px solid var(--fds-outline, #adaeb2);
		border-radius: 8px;
		box-sizing: border-box;
		width: 44px;
		height: 44px;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
	}

	.mr-ellipsis-toggle :global(.table-dropdown img) {
		width: 20px;
		height: 20px;
	}

	.page-size-select {
		width: 140px;
	}
</style>
